import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { daySummaryParts, moneyFromCents, summaryText, type SummaryPart } from '@wodi/domain';
import {
  REPORT_ACCESS,
  SUMMARY_LINK_REPORT,
  type DaySummaryView,
  type SessionView,
} from '@wodi/shared';
import { Button, DateField, PageHeader, PrintTable } from '@wodi/ui';
import { Fragment, useState } from 'react';

import { TableScroll } from '../layout/screen.tsx';
import { fetchDaySummary, reportKeys } from '../api/reports.ts';
import { can, sessionQuery } from '../api/session.ts';
import { money } from '../billing/format.ts';
import { reportHref } from '../reports/catalogue.ts';
import { CATEGORY_LABELS } from '../reports/labels.ts';
import {
  AsAt,
  BackToReports,
  dayOf,
  facilityWords,
  FacilityFilter,
  periodWords,
  PrintableReport,
  ReportActions,
  ReportStates,
  todayIso,
  useFacilityChoice,
  useSheet,
  yesterdayIso,
} from '../reports/report-frame.tsx';
import styles from '../reports/reports.module.css';

type Facility = DaySummaryView['facilities'][number];
type Figures = Facility['figures'];

function whenWords(day: string): string {
  if (day === yesterdayIso()) return 'Yesterday';
  if (day === todayIso()) return 'Today';
  return `On ${periodWords({ from: day, to: day })}`;
}

function partsFor(day: string, figures: Figures): SummaryPart[] {
  return daySummaryParts({
    when: whenWords(day),
    patients: figures.patients,
    billed: moneyFromCents(BigInt(figures.billed.cents)),
    collected: moneyFromCents(BigInt(figures.collected.cents)),
    payerShareUnclaimed: moneyFromCents(BigInt(figures.payerShareUnclaimed.cents)),
    unpricedCount: figures.unpricedCount,
  });
}

/** The sentence, with each number linked to its detail report when the reader may open it. */
function Sentence({
  session,
  day,
  figures,
}: {
  session: SessionView;
  day: string;
  figures: Figures;
}) {
  return (
    <p className={styles.summary}>
      {partsFor(day, figures).map((part, i) => {
        const key = part.link === undefined ? undefined : SUMMARY_LINK_REPORT[part.link];
        const allowed =
          key !== undefined && REPORT_ACCESS[key].permissions.every((p) => can(session, p));
        if (key === undefined || !allowed) return <Fragment key={i}>{part.text}</Fragment>;
        const href = reportHref(key);
        return (
          <Link key={i} to={href} search={key === 'payer-share-unclaimed' ? {} : { day }}>
            {part.text}
          </Link>
        );
      })}
    </p>
  );
}

/**
 * The owner's day summary (DESIGN.md §8; plan §2.11, P5, P6, P13): a written summary first,
 * then compact tables. Aggregates only; no names and no diagnoses.
 */
export function DaySummaryPage({ day: initialDay }: { day?: string | undefined }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const choice = useFacilityChoice('day-summary');
  const [day, setDay] = useState(initialDay ?? yesterdayIso());
  const [facility, setFacility] = useState(session?.facility.id ?? '');
  const [shown, setShown] = useState({ day, facilityIds: choice.idsFor(facility) });
  const report = useQuery({
    queryKey: reportKeys.report('day-summary', shown),
    queryFn: () => fetchDaySummary(shown),
  });
  const sheet = useSheet();
  const data = report.data;
  if (session === null) return null;
  return (
    <div className={styles.page}>
      <BackToReports />
      <PageHeader title="Owner's day summary" />
      <form
        className={styles.filters}
        onSubmit={(e) => {
          e.preventDefault();
          if (dayOf(day) !== null) setShown({ day, facilityIds: choice.idsFor(facility) });
        }}
      >
        <DateField
          label="Day"
          value={day}
          onChange={(v) => {
            if (v !== null) setDay(v);
          }}
        />
        <FacilityFilter reportKey="day-summary" value={facility} onChange={setFacility} />
        <div>
          <Button type="submit" variant="primary">
            Show report
          </Button>
        </div>
      </form>
      <ReportStates query={report} what="day summary" isEmpty={() => false} emptyText="">
        {(d) => (
          <>
            <AsAt dataAsAt={d.dataAsAt} />
            {d.total === null ? null : (
              <section className={styles.section} aria-labelledby="summary-all">
                <h2 id="summary-all" className={styles.sectionHeading}>
                  All facilities
                </h2>
                <p className={styles.summary}>{summaryText(partsFor(d.day, d.total))}</p>
              </section>
            )}
            {d.facilities.map((f) => (
              <FacilitySummary
                key={f.facilityId}
                session={session}
                day={d.day}
                facility={f}
                several={d.facilities.length > 1}
              />
            ))}
          </>
        )}
      </ReportStates>
      {data === undefined ? null : (
        <>
          <ReportActions
            reportKey="day-summary"
            sheet={sheet}
            printBody={{
              period: { from: data.day, to: data.day },
              facilityIds: data.facilities.map((f) => f.facilityId),
              rowCount: data.facilities.length,
            }}
            exportBody={shown}
            disabled={report.isFetching}
          />
          <PrintableReport
            sheet={sheet}
            title="Owner's day summary"
            facility={facilityWords(data.facilities.map((f) => f.name))}
            period={periodWords({ from: data.day, to: data.day })}
            dataAsAt={data.dataAsAt}
          >
            {data.facilities.map((f) => (
              <div key={f.facilityId}>
                {data.facilities.length > 1 ? <h2>{f.name}</h2> : null}
                <p>{summaryText(partsFor(data.day, f.figures))}</p>
                <PrintTable>
                  <CategoryRows facility={f} />
                </PrintTable>
              </div>
            ))}
          </PrintableReport>
        </>
      )}
    </div>
  );
}

function CategoryRows({ facility: f }: { facility: Facility }) {
  return (
    <>
      <thead>
        <tr>
          <th scope="col">Department</th>
          <th scope="col" data-numeric>
            Billed
          </th>
        </tr>
      </thead>
      <tbody>
        {f.byCategory.map((c) => (
          <tr key={c.category}>
            <td>{CATEGORY_LABELS[c.category]}</td>
            <td data-numeric>{money(c.billed)}</td>
          </tr>
        ))}
      </tbody>
    </>
  );
}

function count(n: number, one: string, many: string): string {
  return `${String(n)} ${n === 1 ? one : many}`;
}

function stockWords(s: NonNullable<Facility['stock']>): string {
  return `${count(s.expiredBatches, 'expired batch', 'expired batches')} still in stock, ${count(s.nearExpiryBatches, 'batch', 'batches')} expiring within 90 days, ${count(s.itemsAtOrBelowReorder, 'item', 'items')} at or below the reorder level.`;
}

function waitingWords(w: Facility['waiting']): string {
  if (w.count === 0 || w.medianMinutes === null || w.p90Minutes === null)
    return 'Nobody was seen at a consultation point.';
  return `${String(w.count)} seen at a consultation point. Half waited ${String(w.medianMinutes)} minutes or less; 9 in 10 waited ${String(w.p90Minutes)} minutes or less.`;
}

function FacilitySummary({
  session,
  day,
  facility: f,
  several,
}: {
  session: SessionView;
  day: string;
  facility: Facility;
  several: boolean;
}) {
  const id = `summary-${f.facilityId}`;
  return (
    <section className={styles.summarySection} aria-labelledby={id}>
      <h2 id={id} className={styles.sectionHeading}>
        {several ? f.name : 'Summary'}
      </h2>
      <Sentence session={session} day={day} figures={f.figures} />
      <div className={styles.pair}>
        <div className={styles.block}>
          <h3 className={styles.subHeading}>Revenue by department</h3>
          {f.byCategory.length === 0 ? (
            <p className={styles.muted}>Nothing was billed.</p>
          ) : (
            <TableScroll>
              <table className={styles.table} aria-label="Revenue by department">
                <CategoryRows facility={f} />
              </table>
            </TableScroll>
          )}
        </div>
        <div className={styles.block}>
          <h3 className={styles.subHeading}>Revenue by payer</h3>
          {f.byPayer.length === 0 ? (
            <p className={styles.muted}>Nothing was billed.</p>
          ) : (
            <TableScroll>
              <table className={styles.table} aria-label="Revenue by payer">
                <thead>
                  <tr>
                    <th scope="col">Payer</th>
                    <th scope="col" data-numeric>
                      Billed
                    </th>
                    <th scope="col" data-numeric>
                      Payer share
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {f.byPayer.map((p) => (
                    <tr key={p.payerId}>
                      <td>{p.payerName}</td>
                      <td data-numeric>{money(p.billed)}</td>
                      <td data-numeric>{money(p.payerShare)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
          )}
        </div>
      </div>
      <div className={styles.block}>
        <h3 className={styles.subHeading}>Waiting times</h3>
        <p className={styles.summary}>{waitingWords(f.waiting)}</p>
      </div>
      {f.stock === null ? null : (
        <div className={styles.block}>
          <h3 className={styles.subHeading}>Stock alerts</h3>
          <p className={styles.summary}>
            {`${stockWords(f.stock)} `}
            <Link to="/stores">Open stores</Link>
          </p>
        </div>
      )}
    </section>
  );
}
